import { ChangeEvent, FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateUserLinkedinSettingsMutation } from "@/pages/Private/redux/admin/admin.api";
import { useRecheckUserLinkedinCapabilitiesMutation } from "@/pages/Private/redux/unipile/unipile.api";
import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { User } from "@/pages/Public/pages/Login/schema/login";
import {
	isSalesNavigatorDisconnectedStatus,
	LinkedinCapabilities,
} from "@/pages/Private/pages/Account/schema/unipile";

import { InputField } from "@/components/InputField/InputField";
import { SelectField, SelectFieldOptions } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";

import { AUTO_WITHDRAW_INVITES_DAY_OPTIONS } from "@/pages/Private/pages/Account/components/AutoWithdrawInvites";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { LinkedinCapabilitiesList } from "@/components/LinkedinCapabilities/LinkedinCapabilitiesList";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import {
	UpdateLinkedinSettingsForm,
	UpdateLinkedinSettingsFormSchema,
} from "../schema/update-user";
import { FeaturesCodes } from "../enum/codes";

interface EditLinkedinSettingsProps {
	user: User | null;
	isVisible: boolean;
	handleClose: () => void;
	companyId?: number;
}

export const EditLinkedinSettings: FunctionComponent<EditLinkedinSettingsProps> = ({
	user,
	isVisible,
	handleClose,
}) => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`account.linkedinCapabilities.${key}`);

	const [updateUser, { isLoading, error }] = useUpdateUserLinkedinSettingsMutation();
	const [recheckCapabilities, { isLoading: isRechecking }] =
		useRecheckUserLinkedinCapabilitiesMutation();

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// The `user` prop is a table-row snapshot — a fresh recheck result is kept
	// locally so the newly detected values display immediately (the "Users" tag
	// invalidation refreshes the table behind the modal).
	const [recheckedCapabilities, setRecheckedCapabilities] = useState<LinkedinCapabilities | null>(
		null
	);
	const [hasRecheckError, setHasRecheckError] = useState(false);

	const capabilities = {
		hasLinkedinPremium: recheckedCapabilities?.hasLinkedinPremium ?? user?.hasLinkedinPremium,
		hasLiSn: recheckedCapabilities?.hasLiSn ?? user?.hasLiSn,
		hasAdminRights: recheckedCapabilities?.hasAdminRights ?? user?.hasAdminRights,
	};

	const initialValues: UpdateLinkedinSettingsForm = {
		visitsPerDay: user?.visitsPerDay ?? 0,
		connectionRequestsPerDay: user?.connectionRequestsPerDay ?? 0,
		messagesPerDay: user?.messagesPerDay ?? 0,
		autoWithdrawInvitesEnabled: user?.autoWithdrawInvitesEnabled ?? false,
		autoWithdrawInvitesDays: (user?.autoWithdrawInvitesDays ??
			AUTO_WITHDRAW_INVITES_DAY_OPTIONS[0]) as UpdateLinkedinSettingsForm["autoWithdrawInvitesDays"],
	};

	const {
		handleSubmit,
		setValue,
		formState: { errors },
		watch,
	} = useForm<UpdateLinkedinSettingsForm>({
		defaultValues: initialValues,
		resolver: zodResolver(UpdateLinkedinSettingsFormSchema),
	});

	const formValues = watch();

	const dayOptions: SelectFieldOptions = AUTO_WITHDRAW_INVITES_DAY_OPTIONS.map((days) => ({
		title: t("account.autoWithdrawInvitesDaysOption").replace("{{days}}", String(days)),
		value: days,
	}));

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const handleRecheck = async () => {
		setHasRecheckError(false);

		try {
			const result = await recheckCapabilities({ userId: user?.id ?? 0 }).unwrap();

			setRecheckedCapabilities(result);
		} catch {
			setHasRecheckError(true);
		}
	};

	const onSubmit = async (values: UpdateLinkedinSettingsForm) => {
		const body = {
			visitsPerDay: values.visitsPerDay,
			connectionRequestsPerDay: values.connectionRequestsPerDay,
			messagesPerDay: values.messagesPerDay,
			autoWithdrawInvitesEnabled: values.autoWithdrawInvitesEnabled,
			autoWithdrawInvitesDays: values.autoWithdrawInvitesDays,
		};

		try {
			if (Object.entries(body).length > 0) {
				await updateUser({ id: user?.id ?? 0, body });
			}

			handleClose();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={handleClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isVisible}
			title={"Edit Linkedin Settings"}
		>
			<>
				<div className="flex flex-col">
					<div className="flex flex-col items-start gap-0 mb-4">
						<span className="text-smd text-text mb-0 font-bold">
							{user?.firstName} {user?.lastName}
						</span>
						<span className="text-sm text-text-meta mb-4">
							{user?.unipile?.isLoggedIn ? "Connected" : "Not connected"}
						</span>
					</div>

					{isSequencerActive ? (
						<div className="flex flex-col gap-4 mb-4">
							<InputField
								error={!!errors.visitsPerDay?.message}
								handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
									setValue("visitsPerDay", +e.target.value);
								}}
								label={t("basics.visitsPerDay")}
								name={"visitsPerDay"}
								value={formValues.visitsPerDay ?? 0}
							/>

							<InputField
								error={!!errors.connectionRequestsPerDay?.message}
								handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
									setValue("connectionRequestsPerDay", +e.target.value);
								}}
								label={t("basics.connectionRequestsPerDay")}
								name={"connectionRequestsPerDay"}
								value={formValues.connectionRequestsPerDay ?? 0}
							/>

							<InputField
								error={!!errors.messagesPerDay?.message}
								handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
									setValue("messagesPerDay", +e.target.value);
								}}
								label={t("basics.messagesPerDay")}
								name={"messagesPerDay"}
								value={formValues.messagesPerDay ?? 0}
							/>
						</div>
					) : (
						""
					)}

					<div className="flex flex-col gap-4 mb-4">
						<div className="flex flex-row items-center justify-between gap-4">
							<span className="text-sm text-text font-bold">
								{t("account.enableAutoWithdrawInvites")}
							</span>
							<Toggle
								isChecked={formValues.autoWithdrawInvitesEnabled ?? false}
								name="autoWithdrawInvitesEnabled"
								onChange={() =>
									setValue("autoWithdrawInvitesEnabled", !formValues.autoWithdrawInvitesEnabled)
								}
							/>
						</div>

						{formValues.autoWithdrawInvitesEnabled && (
							<SelectField
								hideEmptyValue
								handleSelect={(value) =>
									setValue(
										"autoWithdrawInvitesDays",
										Number(value) as UpdateLinkedinSettingsForm["autoWithdrawInvitesDays"]
									)
								}
								label={t("account.autoWithdrawInvitesWindow")}
								options={dayOptions}
								selectedOption={
									formValues.autoWithdrawInvitesDays ?? AUTO_WITHDRAW_INVITES_DAY_OPTIONS[0]
								}
							/>
						)}
					</div>

					<div className="flex flex-col gap-4">
						<span className="text-sm text-text font-bold">{tc("title")}</span>

						{user?.unipile?.isLoggedIn ? (
							<>
								<LinkedinCapabilitiesList
									hasAdminRights={capabilities.hasAdminRights || false}
									hasLinkedinPremium={capabilities.hasLinkedinPremium || false}
									hasLiSn={capabilities.hasLiSn || false}
									liSnDisconnected={isSalesNavigatorDisconnectedStatus(user?.unipile?.status)}
								/>

								<p className="text-text-meta text-sm">{tc("description")}</p>

								<div className="flex flex-row items-center gap-4">
									<Button
										color={ButtonColor.ACTION_SECONDARY}
										isLoading={isRechecking}
										size={ButtonSize.M}
										title={tc("recheck")}
										onClick={handleRecheck}
									/>
								</div>

								{hasRecheckError && (
									<p className="text-status-error text-sm">{tc("recheckFailed")}</p>
								)}
							</>
						) : (
							<p className="text-text-meta text-sm">{tc("notConnected")}</p>
						)}
					</div>

					{getAllErrors(error, formErrors).length
						? renderErrorMessages(getAllErrors(error, formErrors))
						: null}
				</div>
			</>
		</Modal>
	);
};
